// Chi tiết một phiên Bói Live đã kết thúc — re-render transcript + quẻ/lá số
// từ dữ liệu đã lưu (shape lưu = shape event WS nên tái dùng LiveTranscript 1:1).
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Clock, Coins, NotebookPen } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LiveTranscript } from "@/components/live/live-transcript";
import { LiveSessionRating } from "@/components/live/live-session-rating";
import { formatMmSs, formatVnd } from "@/components/live/live-controls";
import { getLiveConfig, getLiveSession } from "@/lib/boide-api";
import { AiWatermark } from "@/components/ai-watermark";

const END_REASON_BADGE: Record<string, string> = {
  user_ended: "Hoàn tất",
  insufficient_funds: "Hết số dư",
  max_duration: "Hết giờ",
  upstream_closed: "Gián đoạn",
  error: "Lỗi",
  orphaned: "Gián đoạn",
};

export const Route = createFileRoute("/_authenticated/boi-live/$sessionId")({
  head: () => ({
    meta: [
      { title: "Chi Tiết Phiên Đàm Đạo Trực Tiếp Bằng Giọng Nói - Xem Lại Hội Thoại & Kết Quả" },
      {
        name: "description",
        content: "Xem lại nội dung trò chuyện, quẻ Kinh Dịch và lá số đã lập trong phiên Bói Live.",
      },
    ],
  }),
  component: LiveSessionDetailPage,
});

function LiveSessionDetailPage() {
  const { sessionId } = Route.useParams();
  const detail = useQuery({
    queryKey: ["live-session", sessionId],
    queryFn: () => getLiveSession(Number(sessionId)),
  });
  const config = useQuery({ queryKey: ["live-config"], queryFn: getLiveConfig });

  const s = detail.data;
  const diviner = config.data?.diviners.find((d) => d.id === s?.diviner_id);

  return (
    // Root layout là h-dvh overflow-hidden — trang phải tự tạo vùng cuộn (pattern lich-su)
    <main className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-3xl px-4 py-6">
        <Button variant="ghost" size="sm" asChild className="mb-3 -ml-2">
          <Link to="/boi-live">
            <ArrowLeft className="mr-1 h-4 w-4" /> Bói Live
          </Link>
        </Button>

        {detail.isLoading ? (
          <Skeleton className="h-64 w-full rounded-xl" />
        ) : detail.isError || !s ? (
          <p className="py-10 text-center text-sm text-destructive">
            Không tìm thấy phiên bói này.
          </p>
        ) : (
          <div className="space-y-4">
            <Card className="border-gold-soft/40 bg-card/60">
              <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between py-4">
                <div className="min-w-0">
                  <p className="font-serif-soft text-base sm:text-lg text-gold-gradient font-semibold">
                    Phiên bói với {diviner?.ten ?? s.diviner_id}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {s.started_at ? new Date(s.started_at).toLocaleString("vi-VN") : "—"}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm">
                  <span className="inline-flex items-center gap-1.5 tabular-nums text-muted-foreground">
                    <Clock className="h-3.5 w-3.5 text-primary" /> {formatMmSs(s.duration_seconds)}
                  </span>
                  <span className="inline-flex items-center gap-1.5 tabular-nums text-primary font-medium">
                    <Coins className="h-3.5 w-3.5 text-primary" /> {formatVnd(s.amount_charged)}
                  </span>
                  <Badge variant="outline" className="border-gold-soft/30 font-normal">
                    {END_REASON_BADGE[s.end_reason ?? ""] ?? s.end_reason ?? s.status}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {s.note ? (
              <Card className="border-gold-soft/40 bg-card/60">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <NotebookPen className="h-4 w-4 text-primary" /> Ghi chú buổi xem
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{s.note}</p>
                  <AiWatermark />
                </CardContent>
              </Card>
            ) : null}

            <Card className="border-gold-soft/40 bg-card/60">
              <CardContent className="py-4">
                <LiveSessionRating
                  sessionId={s.id}
                  initialRating={s.rating}
                  initialComment={s.rating_comment}
                  initialCommentRewardedAt={s.rating_comment_rewarded_at}
                />
              </CardContent>
            </Card>

            <LiveTranscript
              transcript={s.transcript}
              toolResults={s.tool_results}
              divinerName={diviner?.ten ?? "thầy"}
            />
          </div>
        )}
      </div>
    </main>
  );
}
